import React from 'react';
import { StudySummaryProps } from '../types';
import { Check, X, RotateCw, ChevronLeft, Trophy, Star } from 'lucide-react';

const StudySummary: React.FC<StudySummaryProps> = ({
  knownCount,
  unknownCount,
  onStartRevision,
  selectedTopic,
  onBackToTopics
}) => {
  const totalCards = knownCount + unknownCount;
  const hasUnknownCards = unknownCount > 0;
  const successRate = totalCards > 0 ? Math.round((knownCount / totalCards) * 100) : 0;

  return (
    <div className="text-center py-8 max-w-2xl mx-auto">
      {/* Icon */}
      <div className="mb-4">
        {successRate === 100 ? (
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 border-2 border-b-[6px] border-green-300 dark:border-green-700">
            <Star className="w-10 h-10 text-green-500 dark:text-green-400 fill-green-500 dark:fill-green-400" />
          </div>
        ) : (
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 border-2 border-b-[6px] border-blue-300 dark:border-blue-700">
            <Trophy className="w-10 h-10 text-blue-500 dark:text-blue-400" />
          </div>
        )}
      </div>

      {/* Main Completion Message */}
      <h2 className="text-3xl font-bold text-foreground mb-2">
        Hotovo!
      </h2>

      <p className="text-base text-muted-foreground mb-1">
        Dokončil jsi studium všech {totalCards} karet
      </p>
      {selectedTopic && (
        <p className="text-sm font-medium text-primary mb-6">
          z tématu "{selectedTopic.title}"
        </p>
      )}

      {/* Results Card - Duolingo style */}
      <div className="bg-card rounded-2xl border-2 border-b-[6px] border-border p-6 mb-6">
        <h3 className="text-lg font-bold text-foreground mb-5">
          Výsledky studia
        </h3>

        <div className="grid grid-cols-2 gap-4 mb-5">
          {/* Known Cards */}
          <div className="rounded-xl border-2 border-b-[4px] border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 p-4">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
              {knownCount}
            </div>
            <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-green-600 dark:text-green-400">
              <Check className="w-4 h-4" strokeWidth={3} />
              Známé karty
            </div>
            <div className="text-xs text-green-600/70 dark:text-green-400/70 mt-1">
              {successRate}% úspěšnost
            </div>
          </div>

          {/* Unknown Cards */}
          <div className="rounded-xl border-2 border-b-[4px] border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-4">
            <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-1">
              {unknownCount}
            </div>
            <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-red-600 dark:text-red-400">
              <X className="w-4 h-4" strokeWidth={3} />
              Neznámé karty
            </div>
            <div className="text-xs text-red-600/70 dark:text-red-400/70 mt-1">
              {totalCards > 0 ? Math.round((unknownCount / totalCards) * 100) : 0}% k opakování
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-5">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-muted-foreground">
              Celkový pokrok
            </span>
            <span className="text-sm font-bold text-foreground">
              {knownCount} / {totalCards}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div
              className="bg-green-500 dark:bg-green-400 h-3 rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${successRate}%`
              }}
            />
          </div>
        </div>

        {/* Actions */}
        {hasUnknownCards ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground font-medium">
              Zopakuj si {unknownCount} neznámých karet.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={onStartRevision}
                className="
                  inline-flex items-center justify-center
                  px-6 py-3
                  bg-blue-500 hover:bg-blue-600
                  dark:bg-blue-600 dark:hover:bg-blue-700
                  text-white font-bold rounded-2xl
                  border-b-4 border-blue-700 dark:border-blue-800
                  hover:border-blue-800 dark:hover:border-blue-900
                  active:border-b-0 active:translate-y-[4px]
                  transition-all duration-150
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  dark:focus:ring-offset-background
                "
              >
                <RotateCw className="w-5 h-5 mr-2" />
                Začít opakování
              </button>
              {onBackToTopics && (
                <button
                  onClick={onBackToTopics}
                  className="
                    inline-flex items-center justify-center
                    px-6 py-3
                    bg-secondary hover:bg-secondary/80
                    text-foreground font-bold rounded-2xl
                    border-2 border-b-[4px] border-border
                    hover:border-border/80
                    active:border-b-2 active:translate-y-[2px]
                    transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                    dark:focus:ring-offset-background
                  "
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Zpět na témata
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-green-600 dark:text-green-400 font-bold text-lg">
              Perfektní! Všechny karty znáš!
            </p>
            <p className="text-muted-foreground text-sm">
              Skvělá práce! Můžeš pokračovat v dalším studiu.
            </p>
            {onBackToTopics && (
              <button
                onClick={onBackToTopics}
                className="
                  inline-flex items-center justify-center
                  px-6 py-3 mt-2
                  bg-secondary hover:bg-secondary/80
                  text-foreground font-bold rounded-2xl
                  border-2 border-b-[4px] border-border
                  hover:border-border/80
                  active:border-b-2 active:translate-y-[2px]
                  transition-all duration-150
                  focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                  dark:focus:ring-offset-background
                "
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Zpět na témata
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tip */}
      <div className="rounded-xl border-2 border-b-[4px] border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
        <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">
          Tip: Pravidelné opakování pomáhá lépe si zapamatovat nové informace!
        </p>
      </div>
    </div>
  );
};

export default StudySummary;
